import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useId, useState, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { EditModal } from '@nucleus-commerce/admin-kit/forms';
import { releaseSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { Product, StoreProductSchedule } from '@nucleus-commerce/api-client/admin';
import { isApiError, type ApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { collectionKeys } from '@/features/collections';
import {
  ProductStoreStateChip,
  ScheduleLink,
  storeCatalogKeys,
  useProductStoreRows,
  type ProductStoreRow,
} from '@/features/store-catalog';

import { useDeleteProduct } from '../hooks/use-delete-product';

/** What the person types to confirm; a fixed word in every language, so it isn't translated. */
const CONFIRM_WORD = 'DELETE';

const readStoreIds = (error: ApiError): readonly string[] =>
  Array.isArray(error.meta.storeIds)
    ? error.meta.storeIds.filter((id): id is string => typeof id === 'string')
    : [];

/** Why the backend refused, in words; a refusal we have no words for shows its own message. */
function Refusal({ error, rows }: { error: ApiError; rows: readonly ProductStoreRow[] }) {
  const { t } = useTranslation('products');
  const message = useErrorMessage();
  const storeIds = readStoreIds(error);
  const named = rows.filter(({ store }) => storeIds.includes(store.id));
  const stores = named.map(({ store }) => store.name).join(', ');
  const scope = stores
    ? t('masterPage.delete.refusal.inStores', { stores })
    : t('masterPage.delete.refusal.inAStore');

  if (error.code === 'RESOURCE_IN_USE' && error.meta.reason === 'auction') {
    return <>{t('masterPage.delete.refusal.auction', { scope })}</>;
  }
  if (error.code === 'RESOURCE_IN_USE' && error.meta.reason === 'rotation') {
    const schedules = new Map<string, StoreProductSchedule>();
    for (const { row } of named) {
      if (row?.schedule) schedules.set(row.schedule.id, row.schedule);
    }
    const links: ReactNode = [...schedules.values()].map((schedule, index) => (
      <span key={schedule.id}>
        {index > 0 ? ', ' : ''}
        <ScheduleLink schedule={schedule} className="font-medium underline" />
      </span>
    ));
    return schedules.size > 0 ? (
      <>
        {t('masterPage.delete.refusal.rotation', { scope })} {links}
      </>
    ) : (
      <>
        {t('masterPage.delete.refusal.rotationUnnamed', { scope })}{' '}
        <Link to="/org/schedules" className="font-medium underline">
          {t('masterPage.delete.refusal.openSchedules')}
        </Link>
      </>
    );
  }
  if (error.code === 'PERMISSION_DENIED') {
    return <>{t('masterPage.delete.refusal.permission')}</>;
  }
  if (error.code === 'STORE_READ_ONLY') {
    return <>{t('masterPage.delete.refusal.readOnly', { scope })}</>;
  }
  return <>{message(error)}</>;
}

function StoreTable({ product }: { product: Product }) {
  const { t } = useTranslation('products');
  const { rows, isPending } = useProductStoreRows(product.id);
  const inStores = rows.filter(({ row }) => row);
  if (inStores.length === 0) return isPending ? <Skeleton className="h-16" /> : null;
  return (
    <div className="overflow-x-auto rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('masterPage.delete.columns.store')}</TableHead>
            <TableHead>{t('masterPage.delete.columns.state')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {inStores.map(({ store, row }) => (
            <TableRow key={store.id}>
              <TableCell>{store.name}</TableCell>
              <TableCell>
                <ProductStoreStateChip row={row} masterStatus={product.status} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export interface DeleteProductModalProps {
  product: Product;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Sets the form's status to Archived: the save bar then saves it like any other change. */
  onArchive: () => void;
}

function Body({ product, onOpenChange, onArchive }: Omit<DeleteProductModalProps, 'open'>) {
  const { t } = useTranslation(['products', 'common', 'errors']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const remove = useDeleteProduct();
  const { rows } = useProductStoreRows(product.id);
  const [typed, setTyped] = useState('');
  const inputId = useId();
  const error = isApiError(remove.error) ? remove.error : null;

  const confirm = () => {
    remove.mutate(product.id, {
      onSuccess: () => {
        for (const { store } of rows) {
          void queryClient.invalidateQueries({ queryKey: storeCatalogKeys.all(store.id) });
          void queryClient.invalidateQueries({ queryKey: collectionKeys.all(store.id) });
        }
        // The record is gone; a dirty form must not keep us on its page.
        releaseSaveBar();
        void navigate({ to: '/org/products' });
      },
    });
  };

  return (
    <EditModal
      open
      onOpenChange={onOpenChange}
      title={t('products:masterPage.delete.title')}
      description={
        <Trans
          t={t}
          i18nKey="products:masterPage.delete.description"
          count={product.variantCount}
          values={{ title: product.title }}
          components={{ b: <b className="text-foreground font-medium" /> }}
        />
      }
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            disabled={remove.isPending}
            onClick={() => onOpenChange(false)}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={remove.isPending || product.status === 'archived'}
            onClick={onArchive}
          >
            {t('products:masterPage.delete.archiveInstead')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={typed !== CONFIRM_WORD || remove.isPending}
            onClick={confirm}
          >
            {remove.isPending
              ? t('products:masterPage.delete.working')
              : t('products:masterPage.delete.confirm')}
          </Button>
        </>
      }
    >
      {error ? (
        <Alert variant="destructive" role="alert">
          <AlertDescription>
            <Refusal error={error} rows={rows} />
            {error.requestId ? (
              <span className="block text-xs">
                {t('errors:requestIdShort', { requestId: error.requestId })}
              </span>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}
      <StoreTable product={product} />
      <p className="bg-muted/40 rounded-lg border px-3 py-2.5 text-[13px]">
        {t('products:masterPage.delete.kept')}
      </p>
      <p className="border-warning/40 bg-warning/10 rounded-lg border px-3 py-2.5 text-[13px]">
        {t('products:masterPage.delete.archiveHint')}
      </p>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={inputId}>
          {t('products:masterPage.delete.typeLabel', { word: CONFIRM_WORD })}
        </Label>
        <Input
          id={inputId}
          value={typed}
          placeholder={CONFIRM_WORD}
          autoComplete="off"
          onChange={(event) => setTyped(event.target.value)}
        />
      </div>
    </EditModal>
  );
}

/** Deletes the product from the master catalog and every store, once DELETE is typed. */
export function DeleteProductModal({ open, ...props }: DeleteProductModalProps) {
  // Mounted while open, so each opening starts with an empty confirmation and no old error.
  return open ? <Body {...props} /> : null;
}
